/**
 * Load dynamic filter options while preventing a filter from filtering itself.
 *
 * @param {Object} params
 * @param {Function} params.getFilterOptions - API function to fetch filter options
 * @param {Object} [params.filters={}] - Active filter key/value pairs
 * @param {Object} [params.baseParams={}] - Extra params always sent (e.g., show_reviewed)
 * @param {Object} params.filterOptionKeyByFilter - Map of filter key -> options response key
 * @returns {Promise<Object>} merged options response
 */
export const loadSelfExcludingFilterOptions = async ({
  getFilterOptions,
  filters = {},
  baseParams = {},
  filterOptionKeyByFilter,
}) => {
  const activeFilterEntries = Object.entries(filters).filter(([, value]) => value);

  const baseOptions = await getFilterOptions({
    ...baseParams,
    ...Object.fromEntries(activeFilterEntries),
  });

  if (activeFilterEntries.length === 0) {
    return baseOptions;
  }

  const selfExcludedResponses = await Promise.all(
    activeFilterEntries.map(async ([filterName]) => {
      const paramsWithoutSelf = {
        ...baseParams,
        ...Object.fromEntries(
          activeFilterEntries
            .filter(([name]) => name !== filterName)
            .map(([name, value]) => [name, value])
        ),
      };

      const optionsWithoutSelf = await getFilterOptions(paramsWithoutSelf);
      return [filterName, optionsWithoutSelf];
    })
  );

  const mergedOptions = { ...baseOptions };
  selfExcludedResponses.forEach(([filterName, optionsWithoutSelf]) => {
    const optionKey = filterOptionKeyByFilter[filterName];
    if (optionKey) {
      mergedOptions[optionKey] = optionsWithoutSelf[optionKey] || [];
    }
  });

  return mergedOptions;
};
